import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  FaPlus, FaEdit, FaTrash, FaSearch, FaUsers, FaFilter,
  FaChild, FaUserGraduate, FaUser, FaEye, FaFilePdf,
  FaFileExcel, FaBirthdayCake, FaTimes, FaSortAmountDown,
  FaSortAmountUp, FaSort, FaWhatsapp, FaUndoAlt, FaExchangeAlt
} from 'react-icons/fa';
import api from '../../services/api';
import ModalDetalhesIrmandade from '../../components/ModalDetalhesIrmandade';
import { exportarPDF, exportarExcel, exportarAniversariantesPDF, exportarAniversariantesExcel, exportarContatosWhatsAppHTML } from '../../utils/exportHelpers';
import ModalExportWhatsApp from '../../components/ModalExportWhatsApp';
import './IrmandadeComum.css';

const FILTROS_PADRAO = {
  categoria: '',
  congregacao: '',
  tipo_pessoa: '',
  mes_aniversario: '',
  idade_min: '',
  idade_max: '',
};

const STORAGE_KEY_IRMANDADE_LISTA = 'irmandade-lista-ui-v1';

const CAMPOS_ORDENACAO = new Set(['nome_completo', 'categoria', '_idade']);

function carregarUiPersistida() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_IRMANDADE_LISTA);
    if (!raw) return null;
    const p = JSON.parse(raw);
    const filtros = { ...FILTROS_PADRAO, ...(p.filtros && typeof p.filtros === 'object' ? p.filtros : {}) };
    const busca = typeof p.busca === 'string' ? p.busca : '';
    let ordenacao = { campo: 'nome_completo', dir: 'asc' };
    if (p.ordenacao && typeof p.ordenacao === 'object') {
      const campo = CAMPOS_ORDENACAO.has(p.ordenacao.campo) ? p.ordenacao.campo : 'nome_completo';
      const dir = p.ordenacao.dir === 'desc' ? 'desc' : 'asc';
      ordenacao = { campo, dir };
    }
    const mostrarFiltros = Boolean(p.mostrarFiltros);
    return { filtros, busca, ordenacao, mostrarFiltros };
  } catch {
    return null;
  }
}

const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const hoje = new Date();
  const nasc = new Date(dataNascimento);
  let idade = hoje.getFullYear() - nasc.getFullYear();
  if (
    hoje.getMonth() < nasc.getMonth() ||
    (hoje.getMonth() === nasc.getMonth() && hoje.getDate() < nasc.getDate())
  ) {
    idade--;
  }
  return idade;
};

const obterIconeCategoria = (cat) => {
  if (cat === 'crianca') return <FaChild />;
  if (cat === 'jovem') return <FaUserGraduate />;
  if (cat === 'adulto') return <FaUser />;
  return <FaUsers />;
};

const obterClasseCategoria = (cat) => {
  if (cat === 'crianca') return 'criancas';
  if (cat === 'jovem') return 'jovens';
  if (cat === 'adulto') return 'adultos';
  return 'todos';
};

const formatarCategoria = (cat) => {
  if (cat === 'crianca') return 'Criança';
  if (cat === 'jovem') return 'Jovem';
  if (cat === 'adulto') return 'Adulto';
  return cat;
};

const MESES = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const IrmandadeTodos = () => {
  const inicialUi = carregarUiPersistida();
  const { canWriteModule, user } = useAuth();
  const podeEditar = canWriteModule('irmandade');
  const perfilCriancas = user?.permissao === 'reuniao_criancas';

  // ── Dados ────────────────────────────────────────────────────────────────
  const [todos, setTodos] = useState([]);          // lista bruta do servidor
  const [carregando, setCarregando] = useState(true);

  // ── Busca + Filtros (persistidos até "Limpar filtros") ───────────────────
  const [busca, setBusca] = useState(() => inicialUi?.busca ?? '');
  const [filtros, setFiltros] = useState(() => inicialUi?.filtros ?? FILTROS_PADRAO);
  const [mostrarFiltros, setMostrarFiltros] = useState(() => inicialUi?.mostrarFiltros ?? false);

  // ── Ordenação ────────────────────────────────────────────────────────────
  const [ordenacao, setOrdenacao] = useState(() => inicialUi?.ordenacao ?? { campo: 'nome_completo', dir: 'asc' });

  // ── UI ───────────────────────────────────────────────────────────────────
  const [modalDetalhes, setModalDetalhes] = useState({ isOpen: false, membro: null });
  const [carregandoDetalhes, setCarregandoDetalhes] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [modalWhatsApp, setModalWhatsApp] = useState(false);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const buscaRef = useRef(null);

  // Persistir filtros, busca, ordenação e painel ao sair/voltar à página
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY_IRMANDADE_LISTA,
        JSON.stringify({
          filtros,
          busca,
          ordenacao,
          mostrarFiltros,
        })
      );
    } catch (e) {
      console.warn('Não foi possível salvar estado dos filtros da Irmandade:', e);
    }
  }, [filtros, busca, ordenacao, mostrarFiltros]);

  // ── Carregamento (re-busca ao alternar exibição de inativos) ─────────────
  useEffect(() => {
    const carregar = async () => {
      try {
        setCarregando(true);
        const res = await api.get('/irmandade/todos', {
          params: mostrarInativos ? { incluir_inativos: 1 } : {},
        });
        const lista = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        // Calcula e guarda a idade como número para facilitar filtragem
        setTodos(lista.map(m => {
          const idade = m.idade ?? calcularIdade(m.data_nascimento);
          const estadoCivil = m.estado_civil ?? m.estadoCivil ?? 'solteiro';
          const virouMaiorDeDoze = m.categoria === 'crianca' && idade != null && idade >= 12;
          const categoria = virouMaiorDeDoze
            ? (['casado', 'divorciado', 'viuvo'].includes(estadoCivil) ? 'adulto' : 'jovem')
            : m.categoria;

          return { ...m, categoria, _idade: idade };
        }));
      } catch (err) {
        console.error('Erro ao carregar membros:', err);
      } finally {
        setCarregando(false);
      }
    };
    carregar();
  }, [mostrarInativos]);

  // ── Filtragem client-side ────────────────────────────────────────────────
  const membrosFiltrados = useMemo(() => {
    const buscaLower = busca.trim().toLowerCase();
    const rawMin = filtros.idade_min !== '' ? parseInt(filtros.idade_min, 10) : null;
    const rawMax = filtros.idade_max !== '' ? parseInt(filtros.idade_max, 10) : null;
    const idadeMin = rawMin != null && !Number.isNaN(rawMin) ? rawMin : null;
    const idadeMax = rawMax != null && !Number.isNaN(rawMax) ? rawMax : null;
    const mesAniv  = filtros.mes_aniversario !== '' ? parseInt(filtros.mes_aniversario, 10) : null;

    let lista = todos.filter(m => {
      if (filtros.categoria) {
        // Filtra pelo campo categoria do banco, mas reforça a regra de idade:
        // criança = menos de 12 anos completos; jovem/adulto = 12 anos ou mais
        if (m.categoria !== filtros.categoria) return false;
        if (filtros.categoria === 'crianca' && m._idade != null && m._idade >= 12) return false;
        if (filtros.categoria === 'jovem'   && m._idade != null && m._idade < 12) return false;
      }
      if (filtros.congregacao && m.congregacao !== filtros.congregacao) return false;
      if (filtros.tipo_pessoa && (m.tipo_pessoa ?? m.tipoPessoa) !== filtros.tipo_pessoa) return false;

      if (idadeMin !== null && (m._idade == null || m._idade < idadeMin)) return false;
      if (idadeMax !== null && (m._idade == null || m._idade > idadeMax)) return false;

      if (mesAniv !== null) {
        const nasc = m.data_nascimento;
        if (!nasc) return false;
        if (new Date(nasc).getUTCMonth() + 1 !== mesAniv) return false;
      }

      if (buscaLower && !String(m.nome_completo || '').toLowerCase().includes(buscaLower)) return false;

      return true;
    });

    // Ordenação
    lista = [...lista].sort((a, b) => {
      let va = a[ordenacao.campo] ?? '';
      let vb = b[ordenacao.campo] ?? '';
      if (ordenacao.campo === '_idade') { va = a._idade ?? -1; vb = b._idade ?? -1; }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return ordenacao.dir === 'asc' ? -1 : 1;
      if (va > vb) return ordenacao.dir === 'asc' ? 1 : -1;
      return 0;
    });

    return lista;
  }, [todos, busca, filtros, ordenacao]);

  // ── Congregações únicas para o select ────────────────────────────────────
  const congregacoes = useMemo(() => {
    const set = new Set(todos.map(m => m.congregacao).filter(Boolean));
    return [...set].sort();
  }, [todos]);

  // ── Badges de filtros ativos ─────────────────────────────────────────────
  const filtrosAtivos = useMemo(() => {
    const badges = [];
    if (filtros.categoria) badges.push({ label: formatarCategoria(filtros.categoria), campo: 'categoria' });
    if (filtros.congregacao) badges.push({ label: filtros.congregacao, campo: 'congregacao' });
    if (filtros.tipo_pessoa) badges.push({ label: filtros.tipo_pessoa === 'irmao' ? 'Irmão' : 'Irmã', campo: 'tipo_pessoa' });
    if (filtros.mes_aniversario) badges.push({ label: `Aniv. ${MESES[parseInt(filtros.mes_aniversario)]}`, campo: 'mes_aniversario' });
    if (filtros.idade_min !== '' || filtros.idade_max !== '') {
      const de = filtros.idade_min !== '' ? `${filtros.idade_min}` : '0';
      const ate = filtros.idade_max !== '' ? `${filtros.idade_max}` : '∞';
      badges.push({ label: `${de}–${ate} anos`, campo: 'idade' });
    }
    return badges;
  }, [filtros]);

  // ── Handlers (filtros aplicados em tempo real) ─────────────────────────
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros(FILTROS_PADRAO);
    setBusca('');
  };

  const removerFiltro = (campo) => {
    if (campo === 'idade') {
      setFiltros(prev => ({ ...prev, idade_min: '', idade_max: '' }));
    } else {
      setFiltros(prev => ({ ...prev, [campo]: '' }));
    }
  };

  const toggleOrdenacao = (campo) => {
    setOrdenacao(prev =>
      prev.campo === campo
        ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { campo, dir: 'asc' }
    );
  };

  const iconeOrdem = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.35 }} />;
    return ordenacao.dir === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />;
  };

  // ── Exportação ───────────────────────────────────────────────────────────
  const handleExportarPDF = async () => {
    try {
      setExportando(true);
      if (filtros.mes_aniversario) {
        await exportarAniversariantesPDF(membrosFiltrados, filtros.mes_aniversario);
      } else {
        await exportarPDF(membrosFiltrados, 'Lista de Membros da Irmandade');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao exportar PDF');
    } finally {
      setExportando(false);
    }
  };

  const handleExportarExcel = async () => {
    try {
      setExportando(true);
      if (filtros.mes_aniversario) {
        await exportarAniversariantesExcel(membrosFiltrados, filtros.mes_aniversario);
      } else {
        await exportarExcel(membrosFiltrados, 'Lista_Membros_Irmandade');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao exportar Excel');
    } finally {
      setExportando(false);
    }
  };

  const handleExportarWhatsApp = (opcoes) => {
    exportarContatosWhatsAppHTML(membrosFiltrados, {
      ...opcoes,
      titulo: 'Contatos Irmandade',
    });
  };

  // ── Modal ────────────────────────────────────────────────────────────────
  const abrirModalDetalhes = async (id) => {
    try {
      setCarregandoDetalhes(true);
      const res = await api.get(`/irmandade/${id}`);
      setModalDetalhes({ isOpen: true, membro: res.data });
    } catch {
      alert('Erro ao carregar detalhes do membro');
    } finally {
      setCarregandoDetalhes(false);
    }
  };

  const excluirMembro = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir ${nome}?`)) return;
    try {
      await api.delete(`/irmandade/${id}`);
      setTodos(prev => prev.filter(m => m.id !== id));
    } catch {
      alert('Erro ao excluir membro');
    }
  };

  const reativarMembro = async (id, nome) => {
    if (!window.confirm(`Reativar ${nome}? Voltará a ser contado em listas, recitativos e relatórios.`)) return;
    try {
      await api.patch(`/irmandade/${id}/reativar`);
      setTodos(prev => prev.map(m => m.id === id ? { ...m, mudou_se: false, ativo: true } : m));
    } catch {
      alert('Erro ao reativar membro');
    }
  };

  const obterCargosMembro = (membro) => {
    if (membro.cargos_nomes) {
      return String(membro.cargos_nomes).split(',').map(c => c.trim()).filter(Boolean);
    }
    const ic = membro.irmandade_cargos || membro.irmandadeCargos;
    if (Array.isArray(ic)) {
      return ic.map(x => x.cargo?.nome).filter(Boolean);
    }
    return [];
  };

  const rotuloTipoPessoa = (membro) => {
    const t = membro.tipo_pessoa ?? membro.tipoPessoa;
    if (t === 'irmao') return '♂ Irmão';
    if (t === 'irma') return '♀ Irmã';
    return '–';
  };

  const temFiltroAtivo = filtrosAtivos.length > 0 || busca.trim();

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="irmandade-categoria-container">

      {/* ── Cabeçalho ─────────────────────────────────────────────────────── */}
      <div className="categoria-header">
        <div className="header-info">
          <h2>
            {perfilCriancas ? <FaChild className="categoria-icon criancas" /> : <FaUsers className="categoria-icon todos" />}
            {perfilCriancas ? 'Irmandade — Crianças' : 'Irmandade'}
          </h2>
          <p>
            {carregando ? 'Carregando…' : `${membrosFiltrados.length} de ${todos.length} membro(s)`}
            {temFiltroAtivo && ' · filtros ativos'}
          </p>
        </div>

        <div className="header-actions">
          {/* Busca rápida */}
          <div className="irmandade-busca-rapida" role="search">
            <FaSearch className="irmandade-busca-icone" aria-hidden />
            <input
              ref={buscaRef}
              type="search"
              className="irmandade-busca-input"
              placeholder="Buscar por nome…"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              aria-label="Buscar membro por nome"
            />
            {busca && (
              <button className="busca-limpar-btn" onClick={() => setBusca('')} title="Limpar busca">
                <FaTimes size={12} />
              </button>
            )}
          </div>

          {/* Exportação */}
          <button
            className="btn-exportar btn-pdf"
            onClick={handleExportarPDF}
            disabled={exportando || membrosFiltrados.length === 0}
            title="Exportar em PDF"
          >
            <FaFilePdf /> PDF
          </button>
          <button
            className="btn-exportar btn-excel"
            onClick={handleExportarExcel}
            disabled={exportando || membrosFiltrados.length === 0}
            title="Exportar em Excel"
          >
            <FaFileExcel /> Excel
          </button>
          <button
            className="btn-exportar btn-whatsapp"
            onClick={() => setModalWhatsApp(true)}
            disabled={membrosFiltrados.length === 0}
            title="Exportar links WhatsApp"
          >
            <FaWhatsapp /> WhatsApp
          </button>

          {/* Mostrar quem mudou-se / inativos */}
          <button
            type="button"
            className={`btn-filtros${mostrarInativos ? ' btn-filtros-ativo btn-filtros-aberto' : ''}`}
            onClick={() => setMostrarInativos(v => !v)}
            title={mostrarInativos ? 'Ocultar quem mudou-se' : 'Mostrar quem mudou-se'}
            aria-pressed={mostrarInativos}
          >
            <FaExchangeAlt />
            {mostrarInativos ? 'Ocultar mudou-se' : 'Mostrar mudou-se'}
          </button>

          {/* Filtros */}
          <button
            type="button"
            className={`btn-filtros${filtrosAtivos.length > 0 ? ' btn-filtros-ativo' : ''}${mostrarFiltros ? ' btn-filtros-aberto' : ''}`}
            onClick={() => setMostrarFiltros(v => !v)}
            aria-expanded={mostrarFiltros}
          >
            <FaFilter />
            Filtros
            {filtrosAtivos.length > 0 && (
              <span className="filtros-badge">{filtrosAtivos.length}</span>
            )}
          </button>

          {podeEditar && (
            <Link to="/irmandade/cadastro" className="btn-novo">
              <FaPlus /> Novo
            </Link>
          )}
        </div>
      </div>

      {/* ── Badges de filtros ativos ─────────────────────────────────────── */}
      {filtrosAtivos.length > 0 && (
        <div className="filtros-badges-bar">
          {filtrosAtivos.map(f => (
            <span key={f.campo} className="filtro-badge-ativo">
              {f.label}
              <button onClick={() => removerFiltro(f.campo)} title={`Remover filtro ${f.label}`}>
                <FaTimes size={10} />
              </button>
            </span>
          ))}
          <button className="filtros-limpar-todos" onClick={limparFiltros}>
            Limpar tudo
          </button>
        </div>
      )}

      {/* ── Painel de filtros ────────────────────────────────────────────── */}
      {mostrarFiltros && (
        <div className="filtros-container filtros-container-dinamico">
          <p className="filtros-dinamico-dica">
            Os resultados atualizam ao alterar qualquer opção. Suas escolhas permanecem salvas neste navegador até você limpar.
          </p>
          <div className="filtros-grid">

            {/* Categoria — oculto para perfil reuniao_criancas (backend já filtra) */}
            {!perfilCriancas && (
              <div className="filtro-grupo">
                <label>Categoria</label>
                <div className="filtro-categoria-btns">
                  {[
                    { val: '', label: 'Todos', cls: 'todos' },
                    { val: 'crianca', label: 'Crianças', cls: 'criancas' },
                    { val: 'jovem', label: 'Jovens', cls: 'jovens' },
                    { val: 'adulto', label: 'Adultos', cls: 'adultos' },
                  ].map(opt => (
                    <button
                      key={opt.val}
                      className={`filtro-cat-btn ${opt.cls}${filtros.categoria === opt.val ? ' ativo' : ''}`}
                      onClick={() => handleFiltroChange('categoria', opt.val)}
                      type="button"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Faixa etária */}
            <div className="filtro-grupo">
              <label>Faixa Etária (anos)</label>
              <div className="filtro-idade-range">
                <input
                  type="number"
                  min="0"
                  max="120"
                  placeholder="De"
                  value={filtros.idade_min}
                  onChange={e => handleFiltroChange('idade_min', e.target.value)}
                />
                <span className="filtro-idade-sep">–</span>
                <input
                  type="number"
                  min="0"
                  max="120"
                  placeholder="Até"
                  value={filtros.idade_max}
                  onChange={e => handleFiltroChange('idade_max', e.target.value)}
                />
              </div>
            </div>

            {/* Congregação */}
            <div className="filtro-grupo">
              <label>Congregação</label>
              <select
                value={filtros.congregacao}
                onChange={e => handleFiltroChange('congregacao', e.target.value)}
              >
                <option value="">Todas</option>
                {congregacoes.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Tipo */}
            <div className="filtro-grupo">
              <label>Tipo</label>
              <select
                value={filtros.tipo_pessoa}
                onChange={e => handleFiltroChange('tipo_pessoa', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="irmao">Irmão</option>
                <option value="irma">Irmã</option>
              </select>
            </div>

            {/* Aniversariantes */}
            <div className="filtro-grupo">
              <label><FaBirthdayCake /> Aniversariantes do Mês</label>
              <select
                value={filtros.mes_aniversario}
                onChange={e => handleFiltroChange('mes_aniversario', e.target.value)}
              >
                <option value="">Todos os Meses</option>
                {MESES.slice(1).map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="filtros-acoes filtros-acoes-so-limpar">
            <button className="btn-limpar" onClick={limparFiltros} type="button">
              <FaTimes /> Limpar filtros
            </button>
          </div>
        </div>
      )}

      {/* ── Conteúdo principal ──────────────────────────────────────────── */}
      <div className="categoria-content">
        {carregando ? (
          <div className="loading">Carregando membros…</div>
        ) : membrosFiltrados.length === 0 ? (
          <div className="empty-state">
            <FaUsers className="empty-icon" />
            <h3>Nenhum membro encontrado</h3>
            <p>
              {temFiltroAtivo
                ? 'Nenhum resultado com os filtros atuais.'
                : 'Não há membros cadastrados.'}
            </p>
            {temFiltroAtivo ? (
              <button className="btn-limpar" onClick={limparFiltros}>
                <FaTimes /> Limpar filtros
              </button>
            ) : podeEditar ? (
              <Link to="/irmandade/cadastro" className="btn-novo">
                <FaPlus /> Cadastrar Primeiro Membro
              </Link>
            ) : null}
          </div>
        ) : (
          <div className="membros-tabela-container">
            <table className="membros-tabela">
              <thead>
                <tr>
                  <th className="th-sortable" onClick={() => toggleOrdenacao('nome_completo')}>
                    Nome {iconeOrdem('nome_completo')}
                  </th>
                  <th className="th-sortable" onClick={() => toggleOrdenacao('categoria')}>
                    Categoria {iconeOrdem('categoria')}
                  </th>
                  <th>Tipo</th>
                  <th className="th-sortable" onClick={() => toggleOrdenacao('_idade')}>
                    Idade {iconeOrdem('_idade')}
                  </th>
                  <th>Telefone</th>
                  <th className="th-hide-mobile">Congregação</th>
                  <th className="th-hide-mobile">Cargos</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {membrosFiltrados.map(membro => {
                  const cargos = obterCargosMembro(membro);
                  const cls = obterClasseCategoria(membro.categoria);
                  return (
                    <tr key={membro.id} className={`membro-row ${cls}${membro.mudou_se ? ' membro-row-mudou' : ''}`}>
                      <td
                        className="nome-cell nome-cell-clicavel"
                        onClick={() => abrirModalDetalhes(membro.id)}
                        title="Ver detalhes"
                      >
                        <div className="nome-info">
                          <span className="icone-categoria">
                            {obterIconeCategoria(membro.categoria)}
                          </span>
                          <div>
                            <div className="nome-principal">
                              {membro.nome_completo}
                              {membro.mudou_se && <span className="badge-mudou-se"><FaExchangeAlt size={9} /> Mudou-se</span>}
                            </div>
                            <div className="nome-meta-mobile">
                              <span className={`categoria-badge ${cls}`}>{formatarCategoria(membro.categoria)}</span>
                              <span className="tipo-pessoa">{rotuloTipoPessoa(membro)}</span>
                              {membro._idade != null && <span className="idade">{membro._idade} anos</span>}
                              {membro.telefone && <span className="telefone-mini">{membro.telefone}</span>}
                            </div>
                            {membro.email && (
                              <div className="email-secundario">{membro.email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`categoria-badge ${cls}`}>
                          {formatarCategoria(membro.categoria)}
                        </span>
                      </td>
                      <td>
                        <span className="tipo-pessoa">
                          {rotuloTipoPessoa(membro)}
                        </span>
                      </td>
                      <td>
                        <span className="idade">
                          {membro._idade != null ? `${membro._idade} anos` : '–'}
                        </span>
                      </td>
                      <td>{membro.telefone || '–'}</td>
                      <td className="th-hide-mobile">{membro.congregacao || '–'}</td>
                      <td className="th-hide-mobile">
                        <div className="cargos-cell">
                          {(membro.categoria === 'jovem' || membro.categoria === 'adulto') ? (
                            cargos.length > 0 ? (
                              <div className="cargos-lista-tabela">
                                {cargos.slice(0, 2).map((c, i) => (
                                  <span key={i} className="cargo-badge-mini">{c}</span>
                                ))}
                                {cargos.length > 2 && (
                                  <span className="cargo-badge-mini mais" title={cargos.slice(2).join(', ')}>
                                    +{cargos.length - 2}
                                  </span>
                                )}
                              </div>
                            ) : <span className="sem-cargo">–</span>
                          ) : (
                            <span className="nao-aplicavel">N/A</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="acoes-cell">
                          <button
                            className="btn-acao-mini visualizar"
                            onClick={() => abrirModalDetalhes(membro.id)}
                            title="Visualizar"
                            disabled={carregandoDetalhes}
                          >
                            <FaEye />
                          </button>
                          {podeEditar && (
                            <Link
                              to={`/irmandade/editar/${membro.id}`}
                              className="btn-acao-mini editar"
                              title="Editar"
                            >
                              <FaEdit />
                            </Link>
                          )}
                          {podeEditar && membro.mudou_se && (
                            <button
                              className="btn-acao-mini reativar"
                              onClick={() => reativarMembro(membro.id, membro.nome_completo)}
                              title="Reativar"
                            >
                              <FaUndoAlt />
                            </button>
                          )}
                          {podeEditar && !membro.mudou_se && (
                            <button
                              className="btn-acao-mini excluir"
                              onClick={() => excluirMembro(membro.id, membro.nome_completo)}
                              title="Excluir"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModalDetalhesIrmandade
        isOpen={modalDetalhes.isOpen}
        onClose={() => setModalDetalhes({ isOpen: false, membro: null })}
        membro={modalDetalhes.membro}
      />

      <ModalExportWhatsApp
        isOpen={modalWhatsApp}
        onClose={() => setModalWhatsApp(false)}
        onExportar={handleExportarWhatsApp}
        totalMembros={membrosFiltrados.length}
      />
    </div>
  );
};

export default IrmandadeTodos;
